import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { api } from '@/api/client';
import { extractErrorMessage } from '@/api/errors';
import { useAuth } from '@/auth/useAuth';
import { VerifiedBadge } from '@/components/VerifiedBadge';
import { isUserVerified } from '@/constants/kyc-requirements';
import { RoleType, UserListItem, UsersListResponse } from '@/types';

const ROLE_OPTIONS: { value: RoleType | ''; label: string }[] = [
  { value: '', label: 'All roles' },
  { value: 'individual', label: 'Individual' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'store_owner', label: 'Store owner' },
  { value: 'admin', label: 'Admin' },
  { value: 'super_admin', label: 'Super admin' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Any status' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  // These two reach accounts the default listing hides entirely.
  { value: 'pending_deletion', label: 'Pending deletion' },
  { value: 'deleted', label: 'Deleted / purged' },
] as const;

export function UsersListPage() {
  const { hasPermission } = useAuth();
  const canSuspend = hasPermission('users.suspend');
  const [searchParams] = useSearchParams();
  const initialPendingReview = searchParams.get('pending_review') === 'true';

  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleType | ''>('');
  const [statusFilter, setStatusFilter] =
    useState<(typeof STATUS_OPTIONS)[number]['value']>('');
  const [pendingReview, setPendingReview] = useState(initialPendingReview);
  const [data, setData] = useState<UsersListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<number | null>(null);

  const params = useMemo(
    () => ({
      page,
      per_page: perPage,
      ...(search && { search }),
      ...(roleFilter && { role_type: roleFilter }),
      ...(statusFilter && { status: statusFilter }),
      ...(pendingReview && { pending_review: 'true' }),
    }),
    [page, perPage, search, roleFilter, statusFilter, pendingReview]
  );

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<UsersListResponse>('/back-office/users', {
        params,
      });
      setData(res.data);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const onSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const toggleSuspend = async (user: UserListItem) => {
    if (!canSuspend) return;
    const suspended = !!user.suspended_at;
    const verb = suspended ? 'unsuspend' : 'suspend';
    if (
      !window.confirm(`${verb[0].toUpperCase()}${verb.slice(1)} ${user.email}?`)
    ) {
      return;
    }
    let reason: string | undefined;
    if (!suspended) {
      reason = window.prompt('Reason (optional):') ?? undefined;
    }
    setActingId(user.id);
    try {
      await api.post(
        `/back-office/users/${user.id}/${verb}`,
        reason ? { reason } : {}
      );
      await fetchUsers();
    } catch (err: unknown) {
      window.alert(extractErrorMessage(err, `Failed to ${verb}`));
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="px-8 py-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Users</h1>
          <p className="text-sm text-slate-500">
            All accounts across consultant, store-owner, individual, and admin
            roles.
          </p>
        </div>
      </div>

      <form
        onSubmit={onSearchSubmit}
        className="mt-6 flex flex-wrap items-end gap-3 rounded border border-slate-200 bg-white p-4"
      >
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-slate-600">
            Search
          </label>
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Name or email"
            className="mt-1 w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600">
            Role
          </label>
          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value as RoleType | '');
              setPage(1);
            }}
            className="mt-1 rounded border border-slate-300 px-3 py-1.5 text-sm"
          >
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600">
            Status
          </label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(
                e.target.value as (typeof STATUS_OPTIONS)[number]['value']
              );
              setPage(1);
            }}
            className="mt-1 rounded border border-slate-300 px-3 py-1.5 text-sm"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700 pb-1.5">
          <input
            type="checkbox"
            checked={pendingReview}
            onChange={(e) => {
              setPendingReview(e.target.checked);
              setPage(1);
            }}
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
          />
          Needs review
        </label>
        <button
          type="submit"
          className="rounded bg-brand-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
        >
          Apply
        </button>
      </form>

      <div className="mt-6 overflow-hidden rounded border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3">Roles</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-8 text-center text-slate-500"
                >
                  Loading…
                </td>
              </tr>
            )}
            {!loading && error && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-red-600">
                  {error}
                </td>
              </tr>
            )}
            {!loading && !error && data?.data.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-8 text-center text-slate-500"
                >
                  No users match these filters.
                </td>
              </tr>
            )}
            {!loading &&
              !error &&
              data?.data.map((u) => {
                const suspended = !!u.suspended_at;
                // Outranks both suspension and verification in the badge: an
                // account on its way out is the most important thing to know
                // about it.
                const pendingDeletion = !!u.deleted_at;
                return (
                  <tr key={u.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5">
                        <Link
                          to={`/users/${u.id}`}
                          className="font-medium text-brand-600 hover:underline"
                        >
                          {u.first_name} {u.last_name}
                        </Link>
                        {isUserVerified(u) && <VerifiedBadge size="sm" />}
                        {u.pending_docs_count > 0 && (
                          <span
                            title={`${u.pending_docs_count} document${u.pending_docs_count === 1 ? '' : 's'} pending review`}
                            className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800"
                          >
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
                            {u.pending_docs_count} pending
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{u.email}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {u.phone ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {u.roles.length === 0 && (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                        {u.roles.map((r) => (
                          <span
                            key={r.id}
                            className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
                          >
                            {r.role_type}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={clsx(
                          'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                          pendingDeletion
                            ? 'bg-slate-800 text-white'
                            : suspended
                              ? 'bg-red-100 text-red-700'
                              : u.is_verified
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-amber-100 text-amber-700'
                        )}
                      >
                        {pendingDeletion
                          ? 'Deleting'
                          : suspended
                            ? 'Suspended'
                            : u.is_verified
                              ? 'Active'
                              : 'Unverified'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canSuspend ? (
                        <button
                          type="button"
                          onClick={() => toggleSuspend(u)}
                          disabled={actingId === u.id}
                          className={clsx(
                            'rounded border px-3 py-1 text-xs font-medium disabled:opacity-50',
                            suspended
                              ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                              : 'border-red-300 text-red-700 hover:bg-red-50'
                          )}
                        >
                          {actingId === u.id
                            ? '…'
                            : suspended
                              ? 'Unsuspend'
                              : 'Suspend'}
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400">
                          No permission
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {data && data.pagination.total_pages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
          <div>
            Page {data.pagination.page} of {data.pagination.total_pages} —{' '}
            {data.pagination.total} users
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="rounded border border-slate-300 px-3 py-1 disabled:opacity-40"
            >
              Prev
            </button>
            <button
              type="button"
              disabled={page >= data.pagination.total_pages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded border border-slate-300 px-3 py-1 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
